import {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GenericIcons from './generics/GenericIcons';
import VdrWordmark from './brand/VdrWordmark';
import { Modal } from '@mui/material';
import '../css/PublicNavbar.css';
import { btnKey } from '../pages/publicPages/PublicWhatWeDoPage';

const LoginPopup: FunctionComponent<{ toggleMenu: () => void; isMobile: boolean }> = ({
  toggleMenu,
  isMobile
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isLoginLocation = useMemo(
    () => ['/public/login', '/public/sign-up'].includes(location.pathname),
    [location.pathname]
  );

  const navigateLogin = useCallback(() => {
    handleCloseModal();
    navigate('/public/login');
  }, [navigate]);

  const navigateSignUp = useCallback(() => {
    handleCloseModal();
    navigate('/public/sign-up');
  }, [navigate]);

  const isActiveLi = useMemo(
    () => ['/public/login', '/public/sign-up'].includes(location.pathname),
    [location.pathname]
  );

  return (
    <>
      <li
        className={`public-navbar-item vdr-nav-login ${
          isActiveLi ? 'public-navbar-item-activated' : ''
        } ${isLoginLocation ? 'public-navbar-item-login' : ''}`}>
        <button
          type='button'
          className='vdr-nav-btn'
          aria-haspopup='dialog'
          onClick={() => {
            toggleMenu();
            handleOpenModal();
          }}>
          <span className='vdr-nav-label'>
            Iniciar Sesión
            <GenericIcons
              className='public-navbar-login-icon'
              icon={
                isLoginLocation && isMobile
                  ? 'userIconBlack'
                  : isLoginLocation && !isMobile
                  ? 'userIconWhite'
                  : 'userIconBlack'
              }
            />
          </span>
        </button>
      </li>
      <Modal open={openModal} onClose={handleCloseModal}>
        <div className='public-navbar-login-modal-container'>
          <button
            type='button'
            className='public-navbar-login-modal-close-btn'
            aria-label='Cerrar'
            onClick={handleCloseModal}>
            <GenericIcons icon='closeIcon' />
          </button>

          <span className='vdr-eyebrow vdr-eyebrow--bar vdr-nav-modal-eyebrow' aria-hidden='true'>
            Plataforma VisiónDERRED
          </span>

          <h2 className='public-navbar-login-modal-title'>Bienvenidos</h2>

          <p className='public-navbar-login-modal-description'>
            Inicia sesión o crea una cuenta para poder acceder a todas las funcionalidades de
            nuestra plataforma
          </p>

          <div className='public-navbar-login-modal-buttons-container'>
            <button
              className='primary-button-style public-navbar-login-action-btn'
              onClick={navigateLogin}>
              Iniciar sesión
              <span className='vdr-nav-arrow' aria-hidden='true'>
                →
              </span>
            </button>
            <button
              className='secondary-button-style public-navbar-login-action-btn'
              onClick={navigateSignUp}>
              Crear cuenta
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const PublicNavBar: FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  type parentKeys = 'que-hacemos';

  const [activeParent, setActiveParent] = useState<parentKeys | null>(null);

  const items: Array<{
    name: string;
    route: string;
    active: boolean;
    disabled: boolean;
    parent?: {
      key: parentKeys;
      childs: Array<{
        icon: string;
        label: string;
        key: string;
      }>;
    };
  }> = [
    {
      name: 'Inicio',
      route: '/public',
      active: location.pathname === '/' || location.pathname === '/public',
      disabled: false
    },
    {
      name: 'Qué hacemos',
      route: '/public/que-hacemos',
      active: location.pathname === '/public/que-hacemos',
      disabled: false,
      parent: {
        key: 'que-hacemos',
        childs: [
          {
            key: btnKey.acercaDe,
            icon: 'InfoIcon',
            label: 'Acerca de la plataforma'
          },
          {
            key: btnKey.mapa,
            icon: 'mapMarkedSolidLargeIcon',
            label: 'Mapa Interactivo de Redes'
          },
          {
            key: btnKey.gestion,
            icon: 'databaseSolidLargeIcon',
            label: 'Gestión de Datos de Redes'
          },
          {
            key: btnKey.capacidad,
            icon: 'LightbulbFilamentIcon',
            label: 'Capacidad de Alojamiento de Redes'
          },
          {
            key: btnKey.evaluacion,
            icon: 'clipboardListSolidLargeIcon',
            label: 'Evaluación de Conexión de Proyectos'
          },
          {
            key: btnKey.puntos,
            icon: 'LightningIcon',
            label: 'Puntos Robustos de Red'
          },
          {
            key: btnKey.estadisticas,
            icon: 'chartBarSolidLargeIcon',
            label: 'Estadísticas del Sector'
          }
        ]
      }
    },
    {
      name: 'Quiénes somos',
      route: '/public/quienes-somos',
      active: location.pathname === '/public/quienes-somos',
      disabled: false
    },
    {
      name: 'Ver mapa',
      route: '/public/ver-mapa',
      active: location.pathname === '/public/ver-mapa',
      disabled: false
    },
    {
      name: 'Ver mapa de calor',
      route: '/public/mapa-de-calor',
      active: location.pathname === '/public/mapa-de-calor',
      disabled: false
    }
  ];

  const isLoginLocation = useMemo(
    () => ['/public/login', '/public/sign-up'].includes(location.pathname),
    [location.pathname]
  );

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const handleCloseMenu = () => setIsMenuOpen(false);
  const menuRef = useRef<HTMLUListElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        menuButtonRef.current &&
        !menuRef.current.contains(target) &&
        !menuButtonRef.current.contains(target)
      ) {
        handleCloseMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const optionParam = useMemo(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    const option = params.get('option');
    return option;
  }, [location.search]);

  return (
    <div
      className={`public-navbar-container vdr-nav ${
        isLoginLocation ? 'public-navbar-container-login' : ''
      }`}>
      <button
        type='button'
        className='public-navbar-logo vdr-nav-brand'
        onClick={() => navigate('/public')}>
        <VdrWordmark onDark size='md' />
      </button>

      {/* mobile: va antes del menú para que, al abrirlo, el Tab siga hacia sus ítems */}
      <div ref={menuButtonRef} className='public-navbar-menu-toggle'>
        <button
          type='button'
          className='vdr-nav-toggle-btn'
          aria-expanded={isMenuOpen}
          aria-controls='vdr-public-menu'
          aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          onClick={toggleMenu}>
          <GenericIcons icon={isLoginLocation ? 'menuIconWhite' : 'menuIcon'} />
        </button>
      </div>

      <ul
        ref={menuRef}
        id='vdr-public-menu'
        className={`public-navbar-items-container ${
          isMenuOpen
            ? 'public-navbar-items-container-mobile'
            : 'public-navbar-items-container-mobile-hidden'
        }`}>
        {items.map((item, idx) => (
          <Fragment key={`public-navbar-btn-${idx}`}>
            <li
              className={`public-navbar-item ${item.active ? 'public-navbar-item-activated' : ''} ${
                isLoginLocation ? 'public-navbar-item-login' : ''
              }`}>
              <button
                type='button'
                className='vdr-nav-btn'
                aria-current={item.active ? 'page' : undefined}
                aria-expanded={
                  item.parent && isMobile ? activeParent === item.parent.key : undefined
                }
                onClick={
                  !item.disabled
                    ? () => {
                        if (item.parent && isMobile) {
                          const parentKey = item.parent?.key;
                          setActiveParent((prev) => (prev === parentKey ? null : parentKey));
                          return;
                        }
                        setActiveParent(null);
                        setIsMenuOpen(false);
                        navigate(item.route);
                      }
                    : undefined
                }>
                <span className='vdr-nav-label'>{item.name}</span>
                {item.parent && (
                  <GenericIcons
                    className='public-navbar-item-parent-arrow-icon'
                    icon='chevronDownIcon'
                    imageStyle={{
                      height: '2vw',
                      ...(activeParent !== item.parent.key ? { transform: 'rotate(-90deg)' } : {})
                    }}
                  />
                )}
              </button>
            </li>
            {item.parent && activeParent === item.parent.key && (
              <div className='public-navbar-item-childs-container'>
                {item.parent.childs.map((c, idxC) => (
                  <li
                    key={`public-navbar-item-child-key-${idxC}`}
                    className={`public-navbar-item-child ${
                      optionParam === c.key ? 'public-navbar-item-child-active' : ''
                    }`}>
                    <button
                      type='button'
                      className='vdr-nav-child-btn'
                      onClick={() => {
                        setActiveParent(null);
                        setIsMenuOpen(false);
                        navigate(`${item.route}?option=${c.key}`);
                      }}>
                      <GenericIcons icon={c.icon} /> <span>{c.label}</span>
                    </button>
                  </li>
                ))}
              </div>
            )}
          </Fragment>
        ))}

        <LoginPopup toggleMenu={handleCloseMenu} isMobile={isMobile} />
      </ul>
    </div>
  );
};

export default PublicNavBar;
